/**
 * Privacy Policy, version 1 (GDP-079, R1.9).
 *
 * Per CLAUDE.md §12 (versioning rule): never edit the text of a
 * published version. To revise, add `privacy-policy-v2.ts` and bump
 * `LEGAL_VERSIONS.privacyPolicy` in `apps/api/src/legal/versions.ts`.
 * Old consent records keep pointing at v1 + the web shell prompts
 * affected clients to re-acknowledge on next sign-in.
 */

export const PRIVACY_POLICY_VERSION = '2026-05-10-v1';

export const PRIVACY_POLICY_DE = `# Datenschutzerklärung

**Version**: \`2026-05-10-v1\`
**Letzte Aktualisierung**: 10. Mai 2026
**Verantwortlicher**: SleepTrust Coaching · Michael Hildebrandt · DE

## 1. Wer wir sind

SleepTrust Coaching ("wir", "uns", "unser") betreibt die H³eroMap™-Coaching-Plattform. Verantwortlicher für Deine personenbezogenen Daten gemäß DSGVO ist SleepTrust Coaching, vertreten durch Michael Hildebrandt, Deutschland. Kontakt: support@sleepercising.com.

## 2. Welche personenbezogenen Daten wir verarbeiten

- **Konto-Daten**: Name, E-Mail-Adresse, Passwort (bcrypt-Hash, niemals im Klartext), Sprachpräferenz, Zeitzone.
- **Coaching-Reise-Daten**: Antworten in den 9-Schritt-Coaching-Werkzeugen, tägliche Tracking-Einträge, KPI-Werte, ausgewählte Tugenden/Werte, Heroic Operating System (HOS), versiegelte Briefe an das zukünftige Ich.
- **Tracking & Kennzahlen**: Bookend-Erfüllung, Fulfillment-Scores, Sitzungsteilnahme, Tagestyp (Good/Fallback)-Einträge.
- **Coach-Interaktionen**: Session-Notizen, Action Items, Phase-Gate-Entscheidungen.
- **Technische Daten**: IP-Adresse (Audit + Rate-Limit), Browser-/App-User-Agent, Login-Zeitstempel.

## 3. Warum wir sie verarbeiten (Rechtsgrundlage)

- **Coaching-Programm betreiben** (Konto, Werkzeuge, Tracking) — Vertragserfüllung, DSGVO Art. 6(1)(b).
- **Transaktions-E-Mails** (Einladung, Passwort-Reset) — Vertragserfüllung, DSGVO Art. 6(1)(b).
- **Tracking-Erinnerungen + Meilenstein-E-Mails** — Einwilligung, DSGVO Art. 6(1)(a).
- **KI-gestützte Coaching-Insights** (ab R2.1) — Einwilligung, DSGVO Art. 6(1)(a).
- **Anonyme Analytik** (Anzahlen, Aggregate) — Einwilligung, DSGVO Art. 6(1)(a).
- **Audit + Sicherheits-Logging** — berechtigtes Interesse, DSGVO Art. 6(1)(f).

Du kannst jede einwilligungs-basierte Verarbeitung jederzeit über die Konto-Einstellungen widerrufen.

## 4. Wo Deine Daten liegen

Alle personenbezogenen Daten werden auf EU-Servern (Hetzner, Deutschland) gespeichert. Wir übertragen Deine personenbezogenen Daten nicht außerhalb des EWR, mit der in §6 genannten Ausnahme (OpenRouter, anonymisierter KI-Input).

## 5. Wie lange wir sie speichern

- **Konto + Coaching-Daten als aktiver Klient**: Für die Dauer Deines Engagements.
- **Konto-Daten nach Deaktivierung**: 90 Tage, dann gelöscht (oder früher auf Anfrage).
- **Audit-Log (Auth-Events)**: 12 Monate.
- **Audit-Log (Datenzugriff)**: 24 Monate.
- **Anonymisierte Aggregate**: Unbegrenzt.

Du kannst die Löschung jederzeit über die Konto-Einstellungen anfordern.

## 6. Auftragsverarbeiter

- **Hetzner Online GmbH** (DE) — Hosting (Server, Datenbank, Object Storage). Nur EU-Server.
- **Twilio SendGrid (EU)** — Transaktions- + Erinnerungs-E-Mails. EU-Datenregion.
- **Sentry (Functional Software)** — Fehler-Tracking (PII bereinigt). EU-Datenregion.
- **OpenRouter** (US, ab R2.1) — LLM-Inferenz für KI-Insights, ausschließlich anonymisierter Input. Standardvertragsklauseln (SCCs) + Anonymisierung.

OpenRouter erhält ausschließlich anonymisierte Inputs (kein Name, keine E-Mail, keine identifizierenden Details).

## 7. Deine Rechte unter DSGVO

Du hast das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20), Widerspruch (Art. 21), Widerruf der Einwilligung jederzeit, und Beschwerde bei der zuständigen Aufsichtsbehörde.

## 8. So übst Du Deine Rechte aus

In der App: Einstellungen → "Datenschutz & Einwilligung" zeigt jede Einwilligung + Widerrufs-Toggle; Einstellungen → "Meine Daten exportieren" erzeugt JSON + PDF; Einstellungen → "Konto löschen" startet die 7-Tage-Karenz + 30-Tage-Hard-Delete-Prozedur.

Per E-Mail: support@sleepercising.com.

Wir bearbeiten alle Anfragen innerhalb von 30 Tagen.

## 9. Sicherheit

Verschlüsselung in der Übertragung (TLS 1.3) und im Ruhezustand (AES-256). Bcrypt-Passwort-Hashing. RBAC (Coaches sehen nur ihre zugewiesenen Klienten). Audit-Logging bei jedem Zugriff. Jährlicher externer Penetrationstest.

## 10. Änderungen dieser Erklärung

Wenn wir diese Erklärung aktualisieren, erhöhen wir die Version, senden eine Zusammenfassung per E-Mail und bitten beim nächsten Login um erneute Zustimmung.

## 11. Kontakt

Fragen oder Rechte-Anfragen: **support@sleepercising.com**.
`;

export const PRIVACY_POLICY_EN = `# Privacy Policy

**Version**: \`2026-05-10-v1\`
**Last updated**: 10 May 2026
**Data controller**: SleepTrust Coaching · Michael Hildebrandt · DE

## 1. Who we are

SleepTrust Coaching ("we", "us", "our") operates the H³eroMap™ coaching platform. The data controller responsible for your personal data under the EU GDPR is SleepTrust Coaching, represented by Michael Hildebrandt, Germany. Contact: support@sleepercising.com.

## 2. What personal data we process

- **Account data**: name, email address, password (stored as a bcrypt hash, never as plaintext), language preference, time zone.
- **Coaching journey data**: responses to the 9-step coaching tools, daily tracking entries, KPI scores, virtues/values you select, your Heroic Operating System (HOS), sealed letters to your future self.
- **Tracking & metrics**: bookend completions, fulfillment scores, session attendance, day-type (Good/Fallback) entries.
- **Coach interactions**: session notes, action items, phase-gate decisions.
- **Technical data**: IP address (audit + rate-limit only), browser/app user agent, login timestamps.

## 3. Why we process it (legal basis)

- **Run the coaching program** (account, tools, tracking) — performance of contract, GDPR Art. 6(1)(b).
- **Send transactional emails** (invites, password reset) — performance of contract, GDPR Art. 6(1)(b).
- **Send tracking-reminder + milestone emails** — consent, GDPR Art. 6(1)(a).
- **Generate AI-powered coaching insights** (R2.1+) — consent, GDPR Art. 6(1)(a).
- **Anonymous analytics** (counts, aggregates) — consent, GDPR Art. 6(1)(a).
- **Audit + security logging** — legitimate interest, GDPR Art. 6(1)(f).

You can withdraw any consent-based processing at any time via your account settings.

## 4. Where your data lives

All personal data is stored on EU servers (Hetzner, Germany). We do not transfer your personal data outside the European Economic Area, with the exception listed in §6 (OpenRouter, anonymised AI input).

## 5. How long we keep it

- **Account + coaching data while you are an active client**: for the duration of your engagement.
- **Account data after deactivation**: 90 days, then deleted unless you request earlier.
- **Audit log (auth events)**: 12 months.
- **Audit log (data access)**: 24 months.
- **Anonymised aggregates**: indefinitely.

You can request deletion at any time via your account settings.

## 6. Sub-processors

- **Hetzner Online GmbH** (DE) — hosting (servers, database, object storage). EU-only servers.
- **Twilio SendGrid (EU)** — transactional + reminder emails. EU data region.
- **Sentry (Functional Software)** — error tracking (PII-scrubbed). EU data region.
- **OpenRouter** (US, R2.1+) — LLM inference for AI insights, anonymised input only. Standard contractual clauses (SCCs) + anonymisation.

OpenRouter only receives anonymised inputs (no name, email, or identifying details).

## 7. Your rights under GDPR

You have the right to access (Art. 15), rectification (Art. 16), erasure (Art. 17), restriction (Art. 18), data portability (Art. 20), object (Art. 21), withdraw consent at any time, and lodge a complaint with your local supervisory authority.

## 8. How to exercise your rights

In-app: settings → "Privacy & consent" shows each consent + revoke toggle; settings → "Export my data" generates JSON + PDF; settings → "Delete my account" starts the 7-day grace + 30-day hard-delete procedure.

By email: support@sleepercising.com.

We respond to all requests within 30 days.

## 9. Security

Encryption in transit (TLS 1.3) and at rest (AES-256). Bcrypt password hashing. RBAC (coaches see only their assigned clients). Audit logging on every access. Annual external penetration test.

## 10. Changes to this policy

When we update this policy, we bump the version, email a summary, and prompt you to re-acknowledge during your next sign-in.

## 11. Contact

Questions or rights requests: **support@sleepercising.com**.
`;

export const PRIVACY_POLICY_TEXT = {
  de: PRIVACY_POLICY_DE,
  en: PRIVACY_POLICY_EN,
} as const;
